import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  IconButton, Typography, CloseIcon, Select, MenuItem, LocationAutocomplete
} from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useState } from 'react';
import { api } from 'src/utils';
import { MapFeatureCategories, MapFeatureCountries } from 'src/data';

export default function AddFeatureModal({ onClose }) {
  const isMobile = useIsMobile();

  const [locationName, setLocationName] = useState('');
  const [coords, setCoords] = useState(null);
  const [countryIsoAlpha2, setCountryIsoAlpha2] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    country: '',
    googleMapsUrl: ''
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!coords) {
        alert('Please select a location on the map.');
        setLoading(false);
        return;
      }

      // POST metadata
      const res = await api.post(`/map-features`, {
        title: formData.title || locationName,
        description: formData.description,
        category: formData.category,
        country: formData.country || countryIsoAlpha2,
        googleMapsUrl: formData.googleMapsUrl,
        images: [],
        geometry: { coordinates: [coords.lng, coords.lat] },
      });

      const created = res.data;

      // Upload images if selected
      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          const formDataImage = new FormData();
          formDataImage.append('image', file);

          await api.post(`/map-features/${created.id}/upload-image`, formDataImage, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      onClose();
    } catch (err) {
      console.error(err);
      alert('Failed to create feature. See console.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2, overflow: 'hidden', paddingTop: isMobile ? 'var(--safe-area-top)' : 0 },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ 
          position: 'absolute', 
          top: isMobile ? 'calc(12px + var(--safe-area-top))' : 8, 
          right: 8, 
          backgroundColor: 'rgba(255,255,255,0.8)' 
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle>Add New Pin</DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Location Autocomplete */}
        <LocationAutocomplete
          value={locationName}
          currentCoords={coords}
          onSelect={(loc) => {
            if (loc === null) {
              setLocationName('');
              setCoords(null);
              setCountryIsoAlpha2(null);
            } else {
              setLocationName(loc.label);
              setCoords(loc.coords);
              setCountryIsoAlpha2(loc.countryCode || null);
              // Default title/country from autocomplete if empty
              setFormData(prev => ({
                ...prev,
                title: prev.title || loc.label,
                country: prev.country || loc.countryCode || ''
              }));
            }
          }}
        />

        <TextField
          name="title"
          label="Title"
          value={formData.title}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          name="description"
          label="Description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
        />

        <Select
          name="category"
          value={formData.category}
          onChange={handleChange}
          fullWidth
          displayEmpty
        >
          <MenuItem value="" disabled>Select Category</MenuItem>
          {MapFeatureCategories.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </Select>

        <Select
          name="country"
          value={formData.country}
          onChange={handleChange}
          fullWidth
          displayEmpty
        >
          <MenuItem value="" disabled>Select Country</MenuItem>
          {MapFeatureCountries.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>

        <Box mt={1} mb={2}>
          <Typography variant="caption">Upload one or more images</Typography>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
            multiple
            onChange={(e) => setImageFiles(Array.from(e.target.files))}
          />
        </Box>

        <TextField
          name="googleMapsUrl"
          label="Google Maps URL"
          value={formData.googleMapsUrl}
          onChange={handleChange}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
